import {forwardRef, useRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode} from 'react';
import {ACCENT_TEXT} from '../lib/color';
import {cn} from '../lib/utils';
import type {Testable} from '../lib/testable';
import {Tooltip, TooltipContent, TooltipTrigger} from '../Tooltip';
import {useIsTruncated} from '../lib/useIsTruncated';

export interface TypographyProps extends ComponentPropsWithoutRef<'p'>, Testable {
	variant?: TypographyVariant;
	component?: ElementType;
	align?: TypographyAlign;
	color?: TypographyColor;
	gutterBottom?: boolean;
	noWrap?: boolean;
	isTruncated?: boolean;
	children?: ReactNode;
}

export const Typography = forwardRef<HTMLElement, TypographyProps>((props, ref) => {
	if (props.isTruncated) return <TypographyWithTruncation {...props} ref={ref}/>;
	return <TypographyBase {...props} ref={ref}/>;
});

Typography.displayName = 'Typography';

const TypographyBase = forwardRef<HTMLElement, TypographyProps>(({
	variant = 'body3',
	component,
	align,
	color = 'textPrimary',
	gutterBottom = false,
	noWrap = false,
	testId,
	className,
	children,
	isTruncated: _isTruncated,
	...props
}, ref) => {
	const Component = (component ?? variantElements[variant]) as ElementType;
	// className ahead of the variant styles on purpose: twMerge is last-wins, so the design system owns
	// type and color while layout utilities from the caller still pass through. Moving it last reopens
	// the escape hatch.
	const classes = cn(
		className,
		variantStyles[variant],
		align && alignStyles[align],
		colorStyles[color],
		gutterBottom && 'mb-4',
		noWrap && 'truncate'
	);

	return (
		<Component ref={ref} data-component='Typography' data-testid={testId} className={classes} {...props}>
			{children}
		</Component>
	);
});

TypographyBase.displayName = 'TypographyBase';

const TypographyWithTruncation = forwardRef<HTMLElement, TypographyProps>(({className, children, ...props}, ref) => {
	const internalRef = useRef<HTMLElement>(null);
	const isClipped = useIsTruncated(internalRef);
	const setRefs = (el: HTMLElement | null) => {
		internalRef.current = el;
		if (typeof ref === 'function') ref(el);
		else if (ref) (ref as {current: HTMLElement | null}).current = el;
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<TypographyBase {...props} ref={setRefs} noWrap className={cn('block', className)}>
					{children}
				</TypographyBase>
			</TooltipTrigger>
			{isClipped && <TooltipContent>{children}</TooltipContent>}
		</Tooltip>
	);
});

TypographyWithTruncation.displayName = 'TypographyWithTruncation';

const variantElements: Record<TypographyVariant, ElementType> = {
	heading1: 'h1',
	heading2: 'h2',
	heading3: 'h3',
	subtitle1: 'p',
	subtitle2: 'p',
	overline1: 'p',
	overline2: 'p',
	body1: 'p',
	body2: 'p',
	body3: 'p',
	small1: 'small',
	small2: 'small',
	small3: 'small',
	tag1: 'span',
	tag2: 'span'
};

const variantStyles: Record<TypographyVariant, string> = {
	heading1: 'text-[32px] font-bold leading-10',
	heading2: 'text-2xl font-normal',
	heading3: 'text-xl font-medium',
	subtitle1: 'text-lg font-bold',
	subtitle2: 'text-lg font-medium',
	overline1: 'text-base font-bold',
	overline2: 'text-base font-normal',
	body1: 'text-sm font-bold',
	body2: 'text-sm font-medium',
	body3: 'text-sm font-normal',
	small1: 'text-[12px] font-medium leading-4',
	small2: 'text-[12px] font-normal leading-4',
	small3: 'text-[10px] font-normal leading-4',
	tag1: 'text-[11px] font-bold leading-4',
	tag2: 'text-[11px] font-medium leading-4'
};

const alignStyles: Record<TypographyAlign, string> = {
	left: 'text-left',
	center: 'text-center',
	right: 'text-right',
	justify: 'text-justify'
};

const colorStyles: Record<TypographyColor, string> = {
	...ACCENT_TEXT,
	white: 'text-foreground-white',
	required: 'text-foreground-red',
	textPrimary: 'text-foreground-contrast',
	helperText: 'text-foreground-100',
	textSecondary: 'text-foreground-200',
	textMuted: 'text-foreground-300'
};

export type TypographyVariant =
	'heading1' | 'heading2' | 'heading3' |
	'subtitle1' | 'subtitle2' | 'overline1' | 'overline2' |
	'body1' | 'body2' | 'body3' |
	'small1' | 'small2' | 'small3' |
	'tag1' | 'tag2';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';

export type TypographyColor =
	'primary' | 'white' | 'required' |
	'textPrimary' | 'helperText' | 'textSecondary' | 'textMuted' |
	'critical' | 'medium' | 'low' | 'success' | 'info';
